<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="head-icons ms-2">
          <a id="collapse-header" @click="toggleHeader"><i class="ti ti-chevrons-up"></i></a>
        </div>
      </div>

      <div class="alert alert-warning mb-3">
        Salary-meta loans and savings are legacy. Once an employee has active
        loans or savings in the tables, payroll ignores meta for that employee.
        Convert all active meta rows here before relying on table-driven payroll.
      </div>

      <div class="row g-3 mb-3">
        <div class="col-lg-3 col-md-6" v-for="card in cards" :key="card.label">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="text-muted small mb-1">{{ card.label }}</div>
              <div class="fs-3 fw-bold">{{ card.value }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 shadow-sm">
        <div class="card-body">
          <div class="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
            <div>
              <h5 class="mb-1">Payroll Meta Conversion</h5>
              <p class="text-muted mb-0">
                Convert legacy salary-meta loans and savings into real loans and savings records for the selected payroll month.
              </p>
            </div>
            <div class="d-flex flex-wrap gap-2 align-items-end">
              <div>
                <label class="form-label mb-1">Payroll Month</label>
                <input v-model="period" type="month" class="form-control" />
              </div>
              <button class="btn btn-outline-primary" :disabled="loading" @click="loadPreview">
                Preview
              </button>
              <button
                class="btn btn-primary"
                :disabled="converting || !selectedKeys.length"
                @click="convertSelected"
              >
                Convert Selected
              </button>
              <button
                class="btn btn-dark"
                :disabled="converting || !convertibleRows.length"
                @click="convertAll"
              >
                Convert All
              </button>
            </div>
          </div>

          <div v-if="message" class="alert alert-info py-2">{{ message }}</div>
          <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th style="width: 40px;">
                    <input
                      type="checkbox"
                      :checked="allConvertibleSelected"
                      :disabled="!convertibleRows.length"
                      @change="toggleAll"
                    />
                  </th>
                  <th>Employee</th>
                  <th>Kind</th>
                  <th>Item</th>
                  <th>Monthly Amount</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="8" class="text-center py-4 text-muted">Loading preview...</td>
                </tr>
                <tr v-else-if="!rows.length">
                  <td colspan="8" class="text-center py-4 text-muted">No active payroll meta loans or savings found for this month.</td>
                </tr>
                <tr v-for="row in rows" :key="row.row_key">
                  <td>
                    <input
                      type="checkbox"
                      :disabled="!row.convertible"
                      :checked="selectedKeys.includes(row.row_key)"
                      @change="toggleRow(row.row_key)"
                    />
                  </td>
                  <td>
                    <div class="fw-semibold">{{ row.employee_name || "-" }}</div>
                    <div class="text-muted small">{{ row.employee_code || "-" }}</div>
                  </td>
                  <td>
                    <span class="badge bg-light text-dark text-capitalize">{{ row.kind }}</span>
                  </td>
                  <td>
                    <div class="fw-semibold">{{ row.label }}</div>
                    <div class="text-muted small text-capitalize">{{ row.subtype || "-" }}</div>
                  </td>
                  <td>{{ money(row.monthly_amount || 0) }}</td>
                  <td>{{ row.start_period || "-" }}</td>
                  <td>{{ row.end_period || "-" }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="row.status === 'linked' ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'"
                    >
                      {{ row.status }}
                    </span>
                    <div class="text-muted small mt-1">{{ row.status_reason }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="resultSummary" class="border rounded p-3 mt-3 bg-light">
            <div class="fw-semibold mb-2">Last Conversion Result</div>
            <div class="small">
              Converted: {{ resultSummary.converted }} |
              Skipped: {{ resultSummary.skipped }} |
              Failed: {{ resultSummary.failed }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import payrollMetaConversionService from "@/services/payrollMetaConversion.service";
import { toast } from "@/utils/toast";
import { currentPayrollYYYYMM } from "@/utils/payrollPeriodHelpers";

const title = "Payroll Meta Conversion";
const text = "Payroll Company Settings";
const text1 = "Payroll Meta Conversion";

const period = ref(currentPayrollYYYYMM());
const loading = ref(false);
const converting = ref(false);
const rows = ref([]);
const summary = ref({ rows: 0, convertible: 0, loans: 0, savings: 0, already_linked: 0 });
const selectedKeys = ref([]);
const message = ref("");
const error = ref("");
const resultSummary = ref(null);

const cards = computed(() => [
  { label: "Rows", value: summary.value.rows || 0 },
  { label: "Convertible", value: summary.value.convertible || 0 },
  { label: "Loans", value: summary.value.loans || 0 },
  { label: "Savings", value: summary.value.savings || 0 },
]);

const convertibleRows = computed(() => rows.value.filter((row) => row.convertible));
const allConvertibleSelected = computed(() =>
  convertibleRows.value.length > 0 &&
  convertibleRows.value.every((row) => selectedKeys.value.includes(row.row_key))
);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function money(value) {
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function syncSelection() {
  const allowed = new Set(convertibleRows.value.map((row) => row.row_key));
  selectedKeys.value = selectedKeys.value.filter((key) => allowed.has(key));
}

function toggleRow(key) {
  if (selectedKeys.value.includes(key)) {
    selectedKeys.value = selectedKeys.value.filter((item) => item !== key);
    return;
  }
  selectedKeys.value = [...selectedKeys.value, key];
}

function toggleAll(event) {
  if (event.target.checked) {
    selectedKeys.value = convertibleRows.value.map((row) => row.row_key);
    return;
  }
  selectedKeys.value = [];
}

async function loadPreview() {
  loading.value = true;
  error.value = "";
  message.value = "";
  try {
    const { data } = await payrollMetaConversionService.preview(period.value);
    rows.value = data?.rows || [];
    summary.value = data?.summary || summary.value;
    syncSelection();
    message.value = rows.value.length
      ? "Preview loaded. Review the items before converting."
      : "No active meta-only items were found for that payroll month.";
  } catch (e) {
    error.value = e?.response?.data?.message || "Failed to load conversion preview.";
  } finally {
    loading.value = false;
  }
}

async function runConversion(selectedOnly) {
  converting.value = true;
  error.value = "";
  message.value = "";
  try {
    const payload = {
      period: period.value,
      selected_keys: selectedOnly ? selectedKeys.value : undefined,
    };
    const { data } = await payrollMetaConversionService.commit(payload);
    resultSummary.value = data?.summary || null;
    toast("Payroll meta conversion completed.", "success");
    await loadPreview();
  } catch (e) {
    error.value = e?.response?.data?.message || "Payroll meta conversion failed.";
    toast(error.value, "error");
  } finally {
    converting.value = false;
  }
}

async function convertSelected() {
  await runConversion(true);
}

async function convertAll() {
  selectedKeys.value = convertibleRows.value.map((row) => row.row_key);
  await runConversion(false);
}

onMounted(loadPreview);
</script>
